import { format, isValid } from 'date-fns'
import { useMemo } from 'react'
import { dateAdjustedForTimezone } from '@opallabs/lambda'

type HasScheduledAt = { scheduled_at?: string | null }
type HasMomentRange = {
  scheduled_at?: string | null
  scheduled_end_at?: string | null
}

/**
 * YYYY-MM-DD day key for the column an instant renders in. Must match how
 * `contentMap`/`momentMap` place cards — i.e. `dateAdjustedForTimezone` then
 * `format` — so range/forbidden checks agree with where the card actually is.
 * Naively stripping the date off the ISO string (`split('T')`) keys by the
 * stored UTC offset's day, which drifts from the rendered column for users
 * whose local timezone crosses midnight relative to that offset.
 *
 * A `Date` is assumed already in the column frame and formatted directly.
 * Date-only strings (no time) fall back to the date portion, mirroring
 * `dateAdjustedForTimezone`'s own behavior for those.
 */
export const toDayKey = (
  value: string | Date | null | undefined
): string | null => {
  if (!value) return null
  if (typeof value === 'string') {
    const adjusted = dateAdjustedForTimezone(value)
    return adjusted ? format(adjusted, 'yyyy-MM-dd') : value.split('T')[0]
  }
  return isValid(value) ? format(value, 'yyyy-MM-dd') : null
}

/**
 * Reports whether a content item's launch date falls before its parent
 * moment's start (`isBefore`) or after its end (`isAfter`). The drag-drop
 * layer only blocks pre-moment drops; the post-moment side is surfaced for
 * UI hints (e.g. the trailing-calendar icon in the moment-card content list).
 */
export const useIsContentOutsideMomentRange = (
  content: HasScheduledAt,
  moment: HasMomentRange | undefined
): { isBefore: boolean; isAfter: boolean } => {
  return useMemo(() => {
    if (!moment) return { isBefore: false, isAfter: false }

    const contentDay = toDayKey(content.scheduled_at)
    const momentStartDay = toDayKey(moment.scheduled_at)

    if (!contentDay || !momentStartDay)
      return { isBefore: false, isAfter: false }

    const momentEndDay = toDayKey(moment.scheduled_end_at) ?? momentStartDay

    return {
      isBefore: contentDay < momentStartDay,
      isAfter: contentDay > momentEndDay,
    }
  }, [content, moment])
}

/**
 * Imperative variant used inside drag-drop handlers where a Date is already
 * in hand (e.g. the target day cell) rather than a content object with a
 * string `scheduled_at`.
 */
export const isDateOutsideMomentRange = (
  date: Date,
  moment: HasMomentRange
): { isBefore: boolean; isAfter: boolean } => {
  const targetDay = toDayKey(date)
  const momentStartDay = toDayKey(moment.scheduled_at)

  if (!targetDay || !momentStartDay) return { isBefore: false, isAfter: false }

  const momentEndDay = toDayKey(moment.scheduled_end_at) ?? momentStartDay

  return {
    isBefore: targetDay < momentStartDay,
    isAfter: targetDay > momentEndDay,
  }
}
